import {
    Component,
    Input,
    OnInit,
    signal
} from "@angular/core";

import {
    AgGridModule
} from "ag-grid-angular";

import {
    ColDef,
    FirstDataRenderedEvent,
    GetDataPath,
    IAggFuncParams,
    ICellRendererParams,
    IGroupCellRendererParams,
    ValueFormatterParams
} from "ag-grid-community";

import {
    autobind
} from "src/app/models/decorators.model";

import {
    TranslateService
} from "@ngx-translate/core";

import {
    CASH_MOVEMENTS,
    CHANNELS,
    CLAIMED_PAYMENTS_AMOUNT,
    DEPARTMENTS,
    EXCLUDED_FROM_SUM_GROUPS,
    exempts,
    getVatValue,
    TICKET,
    TOTAL_CATEGORIES,
    UNCLAIMED_PAYMENTS_AMOUNT
} from "src/app/features/dashboard/components/dashboard/dashboard.utils";

import {
    TilbyCurrencyPipe
} from "@tilby/tilby-ui-lib/pipes/tilby-currency";

import {
    DevLogger
} from "src/app/shared/dev-logger";

import {
    AlignedRow,
    CellValue,
    Values
} from "src/app/features/dashboard/models/dashboard.model";

import {
    EntityManagerService
} from "src/app/core";

import {
    Vat
} from "tilby-models";

/** Groups that only collect rows: they show no total... */
const HIDE_GROUP_VALUE: readonly string[] = [DEPARTMENTS, CHANNELS];
/** ...and these ones no count of operations. */
const HIDE_GROUP_COUNT: readonly string[] = [CLAIMED_PAYMENTS_AMOUNT, UNCLAIMED_PAYMENTS_AMOUNT, CASH_MOVEMENTS, ...HIDE_GROUP_VALUE];

const isValues = (value: unknown): value is Values => typeof value === 'object' && value !== null && 'value' in value;

@Component({
    standalone: true,
    imports: [AgGridModule],
    selector: 'app-fiscal-grid',
    templateUrl: './fiscal-grid.component.html',
    styleUrls: ['./fiscal-grid.component.scss']
})
export class FiscalGridComponent implements OnInit {
    /**
     * Each row has an `orgHierarchy` (its path in the tree) and, for every printer, a `Values` object
     * (see `alignDataPrinters`).
     */
    @Input() rowData: AlignedRow[] = [];
    /** The grid is created only when its columns are ready: ag-grid doesn't handle well columns that arrive after the rows. */
    protected readonly ready = signal(false);

    autoGroupColumnDef: ColDef<AlignedRow> = {
        cellRendererParams: (params: ICellRendererParams<AlignedRow, string> & IGroupCellRendererParams<AlignedRow>) => {
            params.value = this.getLabel(params.value, params.data?.orgHierarchy);
            params.suppressCount = true;
            return params;
        },
        minWidth: 200,
        headerName: '',
        suppressHeaderMenuButton: true
    };
    columnTypes: Record<string, ColDef<AlignedRow>> = {
        numberValue: {
            enableValue: true,
            aggFunc: ({values, rowNode}: IAggFuncParams<AlignedRow, Values>): Values => {
                const key = rowNode?.key ?? '';
                const summed = values.filter(v => !v?.excludeFromSum);
                const total = summed.reduce((sum, v) => sum + (Number(v?.value) || 0), 0);
                const count = summed.reduce((sum, v) => sum + (Number(v?.count) || 0), 0) || summed[0]?.parentCount || 0;
                // a child that is not shown (see `justOnce`) makes the group's total meaningless too
                const hasHiddenValue = summed.some(v => v?.value === '');
                return {
                    name: key,
                    value: HIDE_GROUP_VALUE.includes(key) || hasHiddenValue ? '' : total,
                    excludeFromSum: EXCLUDED_FROM_SUM_GROUPS.includes(key),
                    ...(!HIDE_GROUP_COUNT.includes(key) && {count})
                };
            },
        }
    };
    groupDefaultExpanded = 0;
    defaultColDef: ColDef<AlignedRow> = {
        flex: 1,
        minWidth: 150,
        resizable: true,
    };
    vats: Vat[] = [];
    getDataPath: GetDataPath<AlignedRow> = data => data.orgHierarchy;
    // Each Column Definition results in one Column.
    columnDefs: ColDef<AlignedRow>[] = [];

    constructor(
        private tilbyCurrencyPipe: TilbyCurrencyPipe,
        private translateService: TranslateService,
        private entityManagerService: EntityManagerService,
    ) {
    }

    private verifyTranslationExists(str: string){
        return `DASHBOARD.DIALOG.${str.toUpperCase()}`!==this.translateService.instant(`DASHBOARD.DIALOG.${str.toUpperCase()}`);
    }

    /** Text shown in the tree column: user data (departments, channels, tickets) is left as is, everything else is translated. */
    private getLabel(key: string | null | undefined, hierarchy: string[] = []) {
        if (key == null) {
            return key;
        }

        const path = hierarchy.map(label => label.toLowerCase());
        const isUserData = [DEPARTMENTS, CHANNELS, TICKET].some(category => path.includes(category));
        let label = key;

        // ag-grid converts the segments of the tree path to strings, so a vat arrives here as "22", not 22
        if (/^\d+$/.test(label) && TOTAL_CATEGORIES.some(category => path.includes(category))) {
            label = getVatValue(this.vats, label);
        } else if (label !== '--' && !isUserData && this.verifyTranslationExists(label)) {
            label = this.translateService.instant(`DASHBOARD.DIALOG.${label.toUpperCase()}`);
        }

        return Object.values(exempts).includes(label)
            ? label
            : label.capitalize();
    }

    debugLog(...args: unknown[]) {
        DevLogger.debug('[ FiscalGridComponent ]', ...args);
    }

    async ngOnInit() {
        this.vats = await this.entityManagerService.vat.fetchCollectionOffline();
        this.initTable();
        this.ready.set(true);
    }

    onFirstDataRendered(params: FirstDataRenderedEvent) {
        params.api.sizeColumnsToFit();
    }

    @autobind
    valueFormatter(p: ValueFormatterParams<AlignedRow, CellValue>) {
        const value = isValues(p.value) ? p.value.value : p.value;

        const text = typeof value === 'number'
            ? this.tilbyCurrencyPipe.transform(value)
            : value ?? this.tilbyCurrencyPipe.transform(0);
        // the count of operations is shown only next to an amount
        const count = isValues(p.value) && p.value.count != null && typeof value === 'number' && !isNaN(value)
            ? `(${p.value.count || 0})`
            : '';

        return `${text} ${count}`;
    }

    initTable() {
        const printerKeys = Object.keys(this.rowData?.[0] ?? {}).filter(k => k !== 'orgHierarchy');

        this.columnDefs = [
            {field: 'orgHierarchy', hide: true, suppressHeaderMenuButton: true},
            ...printerKeys.map((field): ColDef<AlignedRow> => ({
                field,
                type: 'numberValue',
                suppressHeaderMenuButton: true,
                valueFormatter: this.valueFormatter,
            }))
        ];
    }
}
